/**
 * One chat message — user or assistant.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 *
 * User messages render plain text. Assistant messages render markdown
 * via react-markdown with inline [cite:id] anchors replaced by
 * CitationChip components.
 */
import React, { useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';

import { callUserFunction } from '../../lib/callUserFunction';
import type { ChatMessage as ChatMessageType, Citation } from '../../types/chat';
import type { GenerationImpactScenarioSnapshot } from '../../types/generationImpact';
import { CitationChip, interpolateCitations } from './CitationChip';
import { MessageFeedback } from './MessageFeedback';
import { SaveToModelDialog } from './SaveToModelDialog';

interface Props {
  message: ChatMessageType;
  conversationId: string;
  /** User profile has a companyId — controls share-with-team CTA visibility. */
  canShareWithTeam: boolean;
  /** Click handler for citation chips. */
  onResolveCitation?: (citation: Citation) => void;
  /** Notify the store when the message is mutated (feedback / insightId). */
  onFeedbackUpdate?: (
    messageId: string,
    update: { feedback?: 'positive' | 'negative'; curatedInsightId?: string },
  ) => void;
}

export function ChatMessage({
  message,
  conversationId,
  canShareWithTeam,
  onResolveCitation,
  onFeedbackUpdate,
}: Props): React.JSX.Element {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Render assistant markdown with inline citation chips by splitting on
  // [cite:id] markers; user messages just render content as-is.
  const renderedBody = useMemo(() => {
    if (message.role !== 'assistant') return message.content;
    return interpolateCitations(message.content, message.citations ?? [], onResolveCitation);
  }, [message.role, message.content, message.citations, onResolveCitation]);

  const handleAccept = useCallback(() => setDialogOpen(true), []);

  const handleConfirmSave = useCallback(
    async (shareWithTeam: boolean) => {
      try {
        // Server proxy (`/api/functions/acceptInsight`) — direct callable
        // invocation is blocked by the org's Domain Restricted Sharing policy.
        const data = await callUserFunction<{ ok: true; insightId: string; shared: boolean }>(
          'acceptInsight',
          { conversationId, messageId: message.id, shareWithTeam },
        );
        onFeedbackUpdate?.(message.id, {
          feedback: 'positive',
          curatedInsightId: data.insightId,
        });
        toast.success(
          data.shared
            ? 'Saved to model + shared with team'
            : 'Saved to model — reports will use it',
        );
      } catch (err) {
        toast.error((err as { message?: string })?.message ?? 'Save failed');
      }
    },
    [conversationId, message.id, onFeedbackUpdate],
  );

  const handleDeny = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Server proxy (`/api/functions/denyInsight`).
      await callUserFunction<{ ok: true }>('denyInsight', {
        conversationId,
        messageId: message.id,
      });
      onFeedbackUpdate?.(message.id, { feedback: 'negative' });
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? 'Could not record feedback');
    } finally {
      setBusy(false);
    }
  }, [busy, conversationId, message.id, onFeedbackUpdate]);

  const handleSaveScenario = useCallback(async () => {
    const preview = message.generationScenarioPreview;
    if (!preview || busy) return;
    setBusy(true);
    try {
      const saved = await callUserFunction<{ scenario: GenerationImpactScenarioSnapshot }>(
        'saveGenerationImpactScenario',
        { siteId: preview.input.siteId, input: preview.input },
      );
      toast.success(`Saved generation scenario “${saved.scenario.name}”`);
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? 'Scenario save failed');
    } finally {
      setBusy(false);
    }
  }, [busy, message.generationScenarioPreview]);

  const handleOpenWorkbench = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'generation');
    window.location.assign(url.toString());
  }, []);

  const isAssistant = message.role === 'assistant';
  return (
    <article
      className={`px-4 py-3 ${isAssistant ? 'bg-background border-l-2 border-primary/40' : ''}`}
      data-role={message.role}
      data-testid="chat-message"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-bold uppercase tracking-widest text-text-muted">
          {isAssistant ? 'Datacrate' : 'You'}
        </span>
        <time
          className="text-[8px] uppercase tracking-widest text-text-muted"
          dateTime={message.createdAt}
        >
          {new Date(message.createdAt).toLocaleTimeString()}
        </time>
      </div>
      <div className="prose prose-sm font-mono text-sm text-text-primary max-w-none">
        {isAssistant ? (
          <div className="space-y-2">
            {Array.isArray(renderedBody)
              ? renderedBody.map((part, i) => (
                  typeof part === 'string'
                    ? <ReactMarkdown key={`p-${i}`}>{part}</ReactMarkdown>
                    : <React.Fragment key={`f-${i}`}>{part}</React.Fragment>
                ))
              : <ReactMarkdown>{message.content}</ReactMarkdown>}
          </div>
        ) : (
          <p>{message.content}</p>
        )}
      </div>
      {isAssistant && message.citations && message.citations.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {message.citations.map((c) => (
            <CitationChip key={c.id} citation={c} onResolve={onResolveCitation} />
          ))}
        </div>
      )}
      {isAssistant && message.generationScenarioPreview && (
        <section className="mt-3 border border-border bg-surface-subtle p-3" aria-label="Generation scenario preview">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted">Deterministic scenario</p>
              <p className="mt-1 text-xs font-bold text-text-primary">{message.generationScenarioPreview.input.name}</p>
            </div>
            <span className={`text-[9px] font-bold uppercase ${message.generationScenarioPreview.result.capacity.nMinusOneVerdict === 'pass' ? 'text-success' : 'text-danger'}`}>
              N-1 {message.generationScenarioPreview.result.capacity.nMinusOneVerdict}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
            <div><span className="block text-text-muted">N-1 net</span>{message.generationScenarioPreview.result.capacity.nMinusOneNetMW.toFixed(1)} MW</div>
            <div><span className="block text-text-muted">Load margin</span>{message.generationScenarioPreview.result.capacity.nMinusOneMarginMW.toFixed(1)} MW</div>
            <div><span className="block text-text-muted">Reserve margin</span>{message.generationScenarioPreview.result.capacity.nMinusOneReserveMarginMW.toFixed(1)} MW</div>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => void handleSaveScenario()} disabled={busy} className="border border-primary px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-primary disabled:opacity-50">Save scenario</button>
            <button type="button" onClick={handleOpenWorkbench} className="border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary">Open workbench</button>
          </div>
        </section>
      )}
      {isAssistant && (
        <MessageFeedback
          message={message}
          onAccept={handleAccept}
          onDeny={handleDeny}
          busy={busy}
        />
      )}
      <SaveToModelDialog
        isOpen={dialogOpen}
        question={''}
        answerPreview={message.content}
        canShareWithTeam={canShareWithTeam}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </article>
  );
}
